<script setup>
const props = defineProps({
  show: Boolean,
  game: Object,
});

const emit = defineEmits(["close"]);

// Kiểm tra URL có phải GIF không
const isGif = (url) => {
  if (!url) return false;
  return url.toLowerCase().endsWith('.gif');
};
</script>

<template>
  <div class="popup-detail" v-if="show">
    <!-- overlay -->
    <div class="overlay" @click="emit('close')"></div>

    <!-- popup -->
    <div class="popup">
      <!-- CLOSE BUTTON -->
      <div class="close" @click="emit('close')">✕</div>

      <!-- HEADER INFO -->
      <div class="header">
        <img :src="game.thumbnail || 'https://via.placeholder.com/110'" class="icon" />

        <div class="meta">
          <h2 class="title">{{ game.title || "N/A" }}</h2>

          <div class="sub">
            {{ game.publisher || "Unknown publisher" }} • 
            {{ game.genre?.join(" • ") || "N/A" }}
          </div>

          <div class="rating-row">
            <div class="rating-pill">
              ⭐ {{ game.rating || "N/A" }} Player rating
            </div>
            <div class="rating-pill">
              👤 {{ game.players || "N/A" }} Players rating
            </div>
          </div>

          <button class="play-btn"><a :href="game.play_url" class="play-btn" target="_blank" rel="noopener noreferrer">
			Play now
			</a></button>
        </div>
      </div>

      <!-- CAROUSEL -->
      <div class="carousel">
		  <div class="carousel-inner">
			<img 
			  v-for="img in (game.screenshots || [])"
			  :key="img"
			  :src="img || 'https://via.placeholder.com/160x100'"
			  :class="isGif(img) ? 'gif' : 'thumb'"
			  alt="screenshot"
			/>
			<div v-if="!game.screenshots || game.screenshots.length === 0" style="color:#aaa">
			  N/A
			</div>
		  </div>
		</div>

      <!-- ABOUT GAME -->
      <h3>About game</h3>
      <p class="text-block">{{ game.about || "N/A" }}</p>

      <!-- HOW TO PLAY -->
      <h3>How to play</h3>
      <div class="text-block" v-html="game.how_to_play || 'N/A'"></div>

      <!-- TAG CLOUD -->
      <div class="tag-cloud">
        <div class="tag" v-for="t in (game.tags || ['N/A'])" :key="t">
          {{ t }}
        </div>
      </div>

      <!-- GAME INFO -->
      <h3>Game info</h3>

      <div class="info-grid">
        <div>
          <div class="info-row">
            <span>Age rating</span>
            <b>{{ game.info?.age_rating || "N/A" }}</b>
          </div>

          <div class="info-row">
            <span>Localization</span>
            <b>{{ game.info?.localization || "N/A" }}</b>
          </div>

          <div class="info-row">
            <span>Cloud saves</span>
            <b>{{ game.info?.cloud_save || "N/A" }}</b>
          </div>
        </div>

        <div>
          <div class="info-row">
            <span>Platform</span>
            <b>{{ game.info?.platform || "N/A" }}</b>
          </div>

          <div class="info-row">
            <span>Screen orientation</span>
            <b>{{ game.info?.orientation || "N/A" }}</b>
          </div>

          <div class="info-row">
            <span>Categories</span>
            <b>{{ game.info?.categories?.join(', ') || "N/A" }}</b>
          </div>
        </div>
      </div>

      <div class="btn-row">
        <button class="white-btn">Share</button>
        <button class="white-btn">Feedback</button>
      </div>

      <!-- SIMILAR GAMES -->
      <h3>Similar games</h3>

      <div class="similar-slider">
        <div 
          class="sim-card" 
          v-for="s in (game.similar || [])" 
          :key="s?.title || s"
        >
          <img :src="s?.img || 'https://via.placeholder.com/120x80'" />
          <div class="sim-title">{{ s?.title || "N/A" }}</div>
        </div>

        <div v-if="!game.similar || game.similar.length === 0" style="color:#aaa">
          N/A
        </div>
      </div>

      <!-- PLAYERS SAY -->
      <h3>Players say</h3>

      <div class="say-grid">
        <div 
          class="say-box" 
          v-for="s in (game.players_say || ['N/A'])" 
          :key="s"
        >
          {{ s }}
        </div>
      </div>

      <div class="ai-note">
        Summarized by AI based on the latest player reviews. Accuracy may be inaccurate.
      </div>
    </div>
  </div>
</template>

<style scoped>
.popup-detail{z-index: 999;}
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
}

.popup {
  position: fixed;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 750px;
  background: #222;
  padding: 25px;
  border-radius: 22px;
  color: white;
  max-height: 90vh;
  overflow-y: auto;
}

/* Close button */
.close {
  position: absolute;
  top: 12px;
  right: 16px;
  cursor: pointer;
  font-size: 20px;
}

/* HEADER */
.header {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
}
.icon {
  width: 110px;
  height: 110px;
  border-radius: 18px;
  object-fit: cover;
}
.meta .title {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
}
.sub {
  color: #aaa;
  margin-bottom: 6px;
}
.rating-row {
  display: flex;
  gap: 8px;
  margin-bottom: 15px;
}
.rating-pill {
  background: #333;
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 13px;
}
.play-btn {
  background: #7bf989;
  color: #000;
  padding: 10px 20px;
  border-radius: 14px;
  font-size: 15px;
  border: none;
  cursor: pointer;
}

/* CAROUSEL */
.carousel-inner {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}
.carousel-inner img {
  width: 160px;
  height: 100px;
  border-radius: 8px;
  object-fit: cover;
}

/* SECTIONS */
h3 {
  margin-top: 20px;
  margin-bottom: 8px;
}
.text-block {
  line-height: 1.55;
  color: #ccc;
}

/* TAG CLOUD */
.tag-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 15px 0;
}
.tag {
  background: #333;
  padding: 5px 10px;
  border-radius: 10px;
  font-size: 13px;
}

/* INFO GRID */
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}
.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
}
.info-row span {
  color: #888;
}

/* BUTTON ROW */
.btn-row {
  display: flex;
  gap: 10px;
  margin: 15px 0;
}
.white-btn {
  background: #333;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid #444;
  cursor: pointer;
  color: white;
}

/* SIMILAR GAMES */
.similar-slider {
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 8px;
}
.sim-card img {
  width: 120px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
}
.sim-title {
  margin-top: 5px;
  font-size: 13px;
  color: #ddd;
}

/* PLAYERS SAY */
.say-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.say-box {
  background: #333;
  padding: 12px;
  border-radius: 12px;
}

.ai-note {
  margin-top: 12px;
  font-size: 11px;
  color: #888;
}

.carousel-inner img.thumb {
  object-fit: cover; /* ảnh bình thường */
}

.carousel-inner img.gif {
  object-fit: contain !important; /* GIF hiển thị đầy đủ */
  background: #000; /* optional nền đen */
}

</style>
